import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/PasswordInput";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Clock } from "lucide-react";

export default function LoginPage() {
  const { user, checked, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!checked) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
        Loading…
      </div>
    );
  }
  if (user) return <Navigate to="/timer" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim().toLowerCase(), password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden"
           style={{ background: "linear-gradient(180deg, #0b2c47 0%, #145c8c 100%)" }}>
        <div className="absolute inset-0 opacity-30"
             style={{
               backgroundImage: "radial-gradient(circle at 20% 30%, #00b5cc 0%, transparent 40%), radial-gradient(circle at 80% 70%, #145c8c 0%, transparent 50%)"
             }} />
        <div className="relative z-10 p-12 flex flex-col justify-between text-white w-full">
          <div className="flex items-center gap-3">
            <Clock className="h-8 w-8 text-white" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-5xl font-bold leading-tight tracking-tight" style={{ fontFamily: "Manrope" }}>
              Where time goes,<br />results flow.
            </h1>
            <p className="mt-4 text-slate-200 max-w-md">
              Don’t count the hours. Make the hours count.
            </p>
          </div>
          <div className="text-xs text-slate-300">© Dreamtime Resorts</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <Clock className="h-8 w-8 text-slate-700" strokeWidth={1.5} />
          </div>
          <h2 className="text-3xl font-semibold tracking-tight" style={{ fontFamily: "Manrope" }}>
            Welcome back
          </h2>
          <p className="mt-2 text-sm text-slate-500">Sign in to your time tracking workspace.</p>

          <form className="mt-8 space-y-5" onSubmit={onSubmit} data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                data-testid="login-password-input"
                toggleTestId="login-password-toggle"
              />
            </div>

            {error && (
              <div className="text-sm rounded-md px-3 py-2 bg-red-50 text-red-700 border border-red-200"
                   data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 text-white"
              style={{ background: "#145c8c" }}
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>

            <div className="text-center">
              <a href="/forgot-password"
                 className="text-sm text-[#145c8c] hover:underline"
                 data-testid="forgot-password-link">
                Forgot your password?
              </a>
            </div>
          </form>

          <p className="mt-8 text-xs text-slate-400">
            Need an account? Ask your workspace admin to add you under <span className="font-medium">Members</span>.
          </p>
        </div>
      </div>
    </div>
  );
}
